import React, { useState, useEffect } from 'react';
import { Trash2, Edit3, Filter, Search, X } from 'lucide-react';
import Swal from 'sweetalert2';
import axios from 'axios';

const AllTransactions: React.FC = () => {
  const [transactions, setTransactions] = useState<any[]>([]);
  const [filteredTransactions, setFilteredTransactions] = useState<any[]>([]);
  
  // Filters State
  const [searchUser, setSearchUser] = useState('');
  const [filterType, setFilterType] = useState('ALL');

  // Edit Modal State
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editFormData, setEditFormData] = useState({
    id: '',
    type: 'DEPOSIT',
    amount: '',
    status: 'SUCCESS'
  });

  // 1. Fetch All Transactions from Database
  const fetchTransactions = async () => {
    try {
      const trxRes = await axios.get('https://motilalalgo.com/api/admin/all-transactions');
    //   const trxRes = await axios.get('http://localhost:10001/api/admin/all-transactions');
      if (trxRes.data.success) {
        setTransactions(trxRes.data.transactions);
        setFilteredTransactions(trxRes.data.transactions);
      }
    } catch (error) {
      console.error("Data Fetching Error:", error);
    }
  };

  useEffect(() => {
    fetchTransactions();
  }, []);

  // 2. Real-time Filter Logic
  useEffect(() => {
    let result = transactions;

    // Filter by Type (DEPOSIT/WITHDRAW)
    if (filterType !== 'ALL') {
      result = result.filter((t) => t.type === filterType);
    }

    // Filter by User (Name or Login ID)
    if (searchUser.trim() !== '') {
      const searchKeyword = searchUser.toLowerCase();
      result = result.filter((t) => 
        (t.first_name && t.first_name.toLowerCase().includes(searchKeyword)) ||
        (t.last_name && t.last_name.toLowerCase().includes(searchKeyword)) ||
        (t.login_id && t.login_id.toLowerCase().includes(searchKeyword))
      );
    }

    setFilteredTransactions(result);
  }, [filterType, searchUser, transactions]);

  // 3. Delete Transaction
  const handleDelete = async (id: number) => {
    Swal.fire({
      title: 'Are you sure?',
      text: "Is transaction ko delete karna chahte hain?",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#3b36a4',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Yes, delete it!'
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          const res = await axios.delete(`https://motilalalgo.com/api/admin/transaction/${id}`);
        //   const res = await axios.delete(`http://localhost:10001/api/admin/transaction/${id}`);
          if (res.data.success) {
            Swal.fire('Deleted!', 'Transaction delete ho gayi hai.', 'success');
            fetchTransactions(); // List auto refresh
          }
        } catch (error) {
          Swal.fire('Error!', 'Transaction delete nahi ho payi.', 'error');
        }
      }
    });
  };

  // 4. Open Edit Modal with Data
  const openEditModal = (trx: any) => {
    setEditFormData({
      id: trx.id,
      type: trx.type,
      amount: trx.amount,
      status: trx.status
    });
    setIsEditModalOpen(true);
  };

  // 5. Submit Edited Data to Backend
  const handleEditSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const res = await axios.put(`https://motilalalgo.com/api/admin/transaction/${editFormData.id}`, {
    //   const res = await axios.put(`http://localhost:10001/api/admin/transaction/${editFormData.id}`, {
        type: editFormData.type,
        amount: editFormData.amount,
        status: editFormData.status
      });

      if (res.data.success) {
        Swal.fire('Updated!', 'Transaction successfully update ho gayi hai.', 'success');
        setIsEditModalOpen(false);
        fetchTransactions(); // UI update karne ke liye refresh
      }
    } catch (error) {
      Swal.fire('Error!', 'Transaction update me problem aayi.', 'error');
    }
  };

  return (
    <div className="p-6 space-y-8 bg-slate-50 min-h-screen">
      
      {/* HEADER & FILTERS */}
      <div className="bg-white rounded-3xl p-8 shadow-xl max-w-7xl mx-auto border border-slate-100">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6 pb-4 border-b border-slate-100">
          <div>
            <h2 className="text-xl font-black text-slate-800 tracking-tight">All User Transactions</h2>
            <p className="text-xs font-bold text-slate-400 uppercase tracking-wider mt-1">Manage, Edit or Delete any transaction</p>
          </div>

          <div className="flex flex-wrap items-center gap-3">
            {/* Search Filter */}
            <div className="relative">
              <Search className="absolute left-3 top-3.5 text-slate-400" size={16} />
              <input 
                type="text"
                placeholder="Search User or ID..."
                value={searchUser}
                onChange={(e) => setSearchUser(e.target.value)}
                className="pl-9 pr-4 py-2 bg-slate-50 border border-slate-200 rounded-xl text-sm font-bold text-slate-700 outline-none focus:border-[#3b36a4] w-52"
              />
            </div>

            {/* Type Filter */}
            <div className="flex items-center gap-2 bg-slate-50 border border-slate-200 rounded-xl px-3 py-2">
              <Filter size={14} className="text-slate-400" />
              <select
                value={filterType}
                onChange={(e) => setFilterType(e.target.value)}
                className="bg-transparent text-sm font-bold text-slate-700 outline-none cursor-pointer"
              >
                <option value="ALL">All Types</option>
                <option value="DEPOSIT">Deposits Only</option>
                <option value="WITHDRAW">Withdrawals Only</option>
              </select>
            </div>
          </div>
        </div>

        {/* TRANSACTIONS TABLE */}
        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="bg-slate-50 text-slate-400 uppercase text-[10px] font-black tracking-widest border-b border-slate-100">
                <th className="p-4">Trx ID</th>
                <th className="p-4">Customer Details</th>
                <th className="p-4">Type</th>
                <th className="p-4">Amount</th>
                <th className="p-4">Status</th>
                <th className="p-4">Date</th>
                <th className="p-4 text-center">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 text-sm font-bold text-slate-700">
              {filteredTransactions.length === 0 ? (
                <tr>
                  <td colSpan={7} className="text-center py-8 text-slate-400">No transactions found.</td>
                </tr>
              ) : (
                filteredTransactions.map((trx) => (
                  <tr key={trx.id} className="hover:bg-slate-50/50 transition">
                    <td className="p-4 font-mono text-xs text-[#3b36a4]">{trx.trx_id}</td>
                    <td className="p-4">
                      <div className="font-black text-slate-800">{trx.first_name} {trx.last_name}</div>
                      <div className="text-xs text-slate-400 font-medium">ID: {trx.login_id}</div>
                    </td>
                    <td className="p-4">
                      <span className={`px-2.5 py-1 rounded-lg text-xs font-black ${trx.type === 'DEPOSIT' ? 'bg-emerald-50 text-emerald-600' : 'bg-rose-50 text-rose-600'}`}>
                        {trx.type}
                      </span>
                    </td>
                    <td className="p-4 text-slate-800 font-black">₹{parseFloat(trx.amount).toLocaleString('en-IN')}</td>
                    <td className="p-4">
                      <span className={`px-2.5 py-1 rounded-lg text-xs font-black ${
                        trx.status === 'SUCCESS' ? 'bg-green-100 text-green-700' : 
                        trx.status === 'PENDING' ? 'bg-amber-100 text-amber-700' : 'bg-rose-100 text-rose-700'
                      }`}>
                        {trx.status}
                      </span>
                    </td>
                    <td className="p-4 text-xs text-slate-400">{new Date(trx.created_at).toLocaleDateString('en-IN')}</td>
                    <td className="p-4">
                      <div className="flex items-center justify-center gap-2">
                        <button 
                          onClick={() => openEditModal(trx)}
                          className="p-2 text-blue-600 hover:bg-blue-50 rounded-xl transition"
                          title="Edit"
                        >
                          <Edit3 size={16} />
                        </button>
                        <button 
                          onClick={() => handleDelete(trx.id)}
                          className="p-2 text-rose-600 hover:bg-rose-50 rounded-xl transition"
                          title="Delete"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* EDIT MODAL POPUP */}
      {isEditModalOpen && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-3xl p-6 shadow-2xl w-full max-w-md border border-slate-100 relative">
            <button 
              onClick={() => setIsEditModalOpen(false)}
              className="absolute right-4 top-4 p-2 text-slate-400 hover:bg-slate-100 rounded-xl transition"
            >
              <X size={18} />
            </button>

            <h3 className="text-lg font-black text-slate-800 mb-4 pb-2 border-b border-slate-100 flex items-center gap-2">
              <Edit3 size={18} className="text-[#3b36a4]" /> Edit Transaction
            </h3>

            <form onSubmit={handleEditSubmit} className="space-y-4">
              {/* Type Field */}
              <div>
                <label className="block text-xs font-bold text-slate-500 uppercase tracking-widest mb-1.5">Transaction Type</label>
                <select 
                  value={editFormData.type}
                  onChange={(e) => setEditFormData({ ...editFormData, type: e.target.value })}
                  className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl font-bold text-slate-800 outline-none focus:border-[#3b36a4]"
                >
                  <option value="DEPOSIT">Deposit</option>
                  <option value="WITHDRAW">Withdraw</option>
                </select>
              </div>

              {/* Amount Field */}
              <div>
                <label className="block text-xs font-bold text-slate-500 uppercase tracking-widest mb-1.5">Amount (INR)</label>
                <input 
                  type="number"
                  value={editFormData.amount}
                  onChange={(e) => setEditFormData({ ...editFormData, amount: e.target.value })}
                  className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl font-bold text-slate-800 outline-none focus:border-[#3b36a4]"
                  required
                />
              </div>

              {/* Status Field */}
              <div>
                <label className="block text-xs font-bold text-slate-500 uppercase tracking-widest mb-1.5">Status</label>
                <select 
                  value={editFormData.status}
                  onChange={(e) => setEditFormData({ ...editFormData, status: e.target.value })}
                  className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl font-bold text-slate-800 outline-none focus:border-[#3b36a4]"
                >
                  <option value="SUCCESS">Success</option>
                  <option value="PENDING">Pending</option>
                  <option value="FAILED">Failed</option>
                </select>
              </div>

              <div className="pt-4 flex items-center justify-end gap-2 border-t border-slate-100">
                <button 
                  type="button"
                  onClick={() => setIsEditModalOpen(false)}
                  className="px-4 py-2 bg-slate-100 text-slate-600 font-bold rounded-xl hover:bg-slate-200 transition text-sm"
                >
                  Cancel
                </button>
                <button 
                  type="submit"
                  className="px-5 py-2 bg-[#3b36a4] text-white font-black rounded-xl shadow-md hover:bg-[#2d2882] transition text-sm"
                >
                  Update
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

    </div>
  );
};

export default AllTransactions;